import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Col,
  Container,
  Row,
  Form,
  Table,
  Button,
  InputGroup,
  Card
} from 'react-bootstrap'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import {
  convertDateWithoutTime,
  convertDateWithTime
} from '../../../helpers/constants'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import * as Common from '../../../utils/Common'
import api from '../../../config/AxiosInstance'
import { Download, RotateCcw, ArrowLeft, Search } from 'lucide-react'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import '../../../styles/style.css'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

function DetailMasterRackDetailPage() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const { slotId } = useParams()

  const [filter, setFilter] = useState({
    date_start: '',
    date_end: '',
    q: '',
    document: ''
  })

  const [debouncedQ, setDebouncedQ] = useState('')
  const [debouncedDocument, setDebouncedDocument] = useState('')
  const [stockCards, setStockCards] = useState([])
  const [loading, setLoading] = useState({
    stockCard: false,
    download: false
  })

  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const prevFetchParams = useRef(null)
  const prevFilters = useRef({
    date_start: filter.date_start,
    date_end: filter.date_end,
    q: debouncedQ,
    document: debouncedDocument
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQ(filter.q)
    }, 500)
    return () => clearTimeout(handler)
  }, [filter.q])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDocument(filter.document)
    }, 500)
    return () => clearTimeout(handler)
  }, [filter.document])

  const handleChangeInput = (key, value) => {
    let newFilter = {
      ...filter,
      [key]: value
    }

    if (key === 'date_start' && value && filter.date_end) {
      if (new Date(value) > new Date(filter.date_end)) {
        newFilter.date_end = value
      }
    }

    if (key === 'date_end' && value && filter.date_start) {
      if (new Date(value) < new Date(filter.date_start)) {
        newFilter.date_start = value
      }
    }

    setFilter(newFilter)
  }

  const renderPack = (data) => {
    return (
      <div
        key={data.doc}
        className={data.type === 'out' ? 'text-danger' : 'text-success'}
      >
        {`${data.qty} ${data.uom}`}
      </div>
    )
  }

  const renderResult = (data) => {
    return (
      <div key={data.doc} className="fw-bold">
        {data.current_pack}{' '}
        {`${data.current_pack === 0 ? '' : ` ${data.uom_pack}`}`}
      </div>
    )
  }

  const columns = [
    {
      key: 'date',
      label: 'Date',
      component: (data) => convertDateWithoutTime(data.date)
    },
    { key: 'pallet', label: 'Pallet' },
    { key: 'item_name', label: 'Item' },
    { key: 'batch', label: 'Batch', className: '' },
    { key: 'doc', label: 'Doc', className: '' },
    {
      key: 'pack',
      label: 'In/Out (Base UOM)',
      className: 'text-end',
      component: (data) => renderPack(data)
    },
    {
      key: 'current_pack',
      label: 'Result',
      className: 'text-end',
      component: (data) => renderResult(data)
    }
  ]

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const fetchStockCards = useCallback(async () => {
    if (!filter.date_start || !filter.date_end) {
      setStockCards([])
      return
    }

    if (new Date(filter.date_start) > new Date(filter.date_end)) {
      return
    }

    const currentFilters = {
      date_start: filter.date_start,
      date_end: filter.date_end,
      q: debouncedQ,
      document: debouncedDocument
    }

    const isFilterChanged =
      JSON.stringify(prevFilters.current) !== JSON.stringify(currentFilters)

    const pageToFetch = isFilterChanged ? 1 : meta.current_page

    const params = {
      start_date: convertDateWithTime(filter.date_start, 'YYYY-MM-DD'),
      end_date: convertDateWithTime(filter.date_end, 'YYYY-MM-DD'),
      q: debouncedQ,
      document: debouncedDocument,
      page: pageToFetch,
      per_page: meta.per_page
    }

    const paramsString = JSON.stringify(params)
    if (paramsString === prevFetchParams.current) return

    setLoading((prev) => ({ ...prev, stockCard: true }))
    try {
      const res = await api.get(`/master/slots/stock_card/${slotId}`, {
        params
      })
      const { status, data } = res
      if (status === 200) {
        setStockCards(data.data)
        setMeta((prev) => ({
          ...prev,
          total_page: data.metadata.total_page,
          total_row: data.metadata.total_row,
          current_page: pageToFetch
        }))
        prevFetchParams.current = paramsString
        prevFilters.current = currentFilters
      }
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading((prev) => ({ ...prev, stockCard: false }))
    }
  }, [
    slotId,
    filter.date_start,
    filter.date_end,
    debouncedQ,
    debouncedDocument,
    meta.current_page,
    meta.per_page
  ])

  useEffect(() => {
    fetchStockCards()
  }, [fetchStockCards])

  const handleReset = () => {
    setFilter({
      date_start: '',
      date_end: '',
      q: '',
      document: ''
    })
    setStockCards([])
    setMeta((prev) => ({ ...prev, current_page: 1 }))
    prevFetchParams.current = null
  }

  const handleDownload = async () => {
    try {
      setLoading((prev) => ({ ...prev, download: true }))
      const params = {
        start_date: convertDateWithTime(filter.date_start, 'YYYY-MM-DD'),
        end_date: convertDateWithTime(filter.date_end, 'YYYY-MM-DD'),
        warehouse_id: state?.warehouseId
      }
      const res = await api.get(`/master/slots/stock_card/${slotId}/download`, {
        responseType: 'arraybuffer',
        params
      })
      const { status, data } = res
      if (status === 200) {
        const blob = new Blob([data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute(
          'download',
          `download-stock-card-${state?.code || 'rack'}.xlsx`
        )
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        throw new Error('Something went wrong')
      }
    } catch (error) {
      console.log(error)
      const err =
        error?.response?.data?.errors?.message ||
        error?.message ||
        'An error occurred'
      Common.showPopUpErrorMessage(err)
    } finally {
      setLoading((prev) => ({ ...prev, download: false }))
    }
  }

  return (
    <Container fluid className="px-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="light"
              className=" border d-flex align-items-center justify-content-center bg-white"
              style={{ width: '40px', height: '40px' }}
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={18} className="text-secondary" />
            </Button>
            <div>
              <h3 style={{ ...headerTitleStyle, marginBottom: '0.2rem' }}>
                {state?.code || 'Stock Card'}
              </h3>
              <small className="text-muted fw-bold text-uppercase">
                Stock Card
              </small>
            </div>
          </div>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCcw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <style>
        {`
          .custom-datepicker .react-date-picker__wrapper {
            height: 45px;
            border-radius: 10px !important;
            border: 1px solid #e9ecef !important;
            display: flex;
            align-items: center;
          }
        `}
      </style>
      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Start Date</Form.Label>
              <InputDatePicker
                value={filter.date_start}
                onChange={(value) => handleChangeInput('date_start', value)}
                clearIcon
                className="custom-datepicker"
                maxDate={filter.date_end ? new Date(filter.date_end) : null}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>End Date</Form.Label>
              <InputDatePicker
                value={filter.date_end}
                onChange={(value) => handleChangeInput('date_end', value)}
                clearIcon
                className="custom-datepicker"
                minDate={filter.date_start ? new Date(filter.date_start) : null}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>ITEM/BATCH</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search item or batch..."
                  value={filter.q}
                  onChange={(e) => handleChangeInput('q', e.target.value)}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Document</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search document..."
                  value={filter.document}
                  onChange={(e) =>
                    handleChangeInput('document', e.target.value)
                  }
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row className="mb-3">
        <Col className="text-end">
          {stockCards.length > 0 && (
            <Button
              variant="warning"
              className="rounded px-4 fw-bold text-white"
              onClick={handleDownload}
              disabled={loading.download}
            >
              <Download size={18} className="me-2" />
              {loading.download ? 'Downloading...' : 'Download'}
            </Button>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              borderRadius: '12px',
              overflow: 'hidden',
              fontSize: '0.9rem'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`p-2 align-middle bg-light border-bottom text-uppercase text-secondary ${
                      column.className || ''
                    }`}
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading.stockCard ? (
                <tr>
                  <td colSpan={columns.length} className="text-center p-5">
                    <Loader />
                  </td>
                </tr>
              ) : stockCards.length === 0 ? (
                <RowEmpty colSpan={columns.length} />
              ) : (
                stockCards.map((stockCard, indexStockCard) => (
                  <tr
                    key={`${stockCard.doc}-${indexStockCard}`}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`p-2 align-middle ${column.className || ''}`}
                      >
                        {column.component && column.component(stockCard)}
                        {!column.component && (stockCard[column.key] || '-')}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td className="p-3 border-top-0" colSpan={columns.length}>
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
    </Container>
  )
}

export default DetailMasterRackDetailPage
